import { useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@turnifly/shared/stores'
import { usePomodoroStore } from '@turnifly/shared/stores'
import type { PomodoroSettings, PomodoroTask, PomodoroPreset } from '@turnifly/shared/types'
import {
  fetchPomodoroSettings,
  upsertPomodoroSettings,
  createPomodoroSession,
  fetchPomodoroTasks,
  createPomodoroTask,
  togglePomodoroTask,
  deletePomodoroTask,
  clearCompletedPomodoroTasks,
  fetchPomodoroPresets,
} from '@turnifly/shared/api'
import {
  playAlarm, stopBackgroundAudio, playBackgroundAudio, setBackgroundVolume,
} from '@turnifly/shared/utils'
import { triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'

const pomodoroKeys = {
  settings: (uid: string) => ['pomodoro', 'settings', uid] as const,
  tasks: (uid: string) => ['pomodoro', 'tasks', uid] as const,
  presets: ['pomodoro', 'presets'] as const,
}

// Global flag — DB settings sync qilindi yoki yo'q (har bir hook instance emas!)
let _settingsSynced = false

// ── Settings hook ──

export function usePomodoroSettings(): {
  isLoading: boolean
  saveSettings: (partial: Partial<PomodoroSettings>) => void
} {
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  const { data, isLoading } = useQuery({
    queryKey: pomodoroKeys.settings(userId),
    queryFn: () => fetchPomodoroSettings(userId),
    enabled: !!userId,
    staleTime: Infinity,
  })

  // DB → store faqat BIR MARTA (global flag — barcha hook instancelar uchun umumiy)
  useEffect(() => {
    if (!data || _settingsSynced) return
    _settingsSynced = true
    usePomodoroStore.getState().updateSettings({
      workDuration: data.work_duration,
      shortBreakDuration: data.short_break_duration,
      longBreakDuration: data.long_break_duration,
      sessionsBeforeLongBreak: data.sessions_before_long_break,
      autoStartBreaks: data.auto_start_breaks,
      autoStartWork: data.auto_start_work,
      timerStyle: data.timer_style,
      backgroundType: data.background_type,
      backgroundValue: data.background_value,
      soundEnabled: data.sound_enabled,
      soundType: data.sound_type,
      soundUrl: data.sound_url,
      alarmSound: data.alarm_sound,
      volume: data.volume,
    })
  }, [data])

  // Save to DB (fire-and-forget)
  const saveSettings = useCallback(
    (partial: Partial<PomodoroSettings>): void => {
      if (!userId) return
      upsertPomodoroSettings(userId, partial).catch(() => {})
    },
    [userId],
  )

  return { isLoading, saveSettings }
}

// ── Timer engine hook ──

export function usePomodoroTimer(): void {
  const status = usePomodoroStore((s) => s.status)
  const tick = usePomodoroStore((s) => s.tick)
  const timeRemaining = usePomodoroStore((s) => s.timeRemaining)
  const mode = usePomodoroStore((s) => s.mode)
  const skipToNext = usePomodoroStore((s) => s.skipToNext)
  const startTimer = usePomodoroStore((s) => s.startTimer)
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const qc = useQueryClient()
  const completedRef = useRef(false)

  // Ref for settings — effect'lar dependency'siz o'qiydi
  const stateRef = useRef(usePomodoroStore.getState())
  useEffect(() => {
    return usePomodoroStore.subscribe((state) => {
      stateRef.current = state
    })
  }, [])

  // Mount bo'lganda — agar timer running bo'lsa, avtomatik PAUSE qil
  // (Mini app yopilib qayta ochilganda tab va tab-bar ko'rinishi uchun)
  useEffect(() => {
    const s = usePomodoroStore.getState()
    if (s.status === 'running' && s.endTime) {
      const remaining = Math.max(0, Math.ceil((s.endTime - Date.now()) / 1000))
      if (remaining <= 0) {
        usePomodoroStore.setState({ timeRemaining: 0, status: 'paused', endTime: null })
      } else {
        usePomodoroStore.setState({ timeRemaining: remaining, status: 'paused', endTime: null })
      }
    }
  }, [])

  // Tick interval
  useEffect(() => {
    if (status !== 'running') return
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [status, tick])

  // Timer completion
  useEffect(() => {
    if (timeRemaining > 0 || status !== 'running') {
      completedRef.current = false
      return
    }
    if (completedRef.current) return
    completedRef.current = true

    const s = stateRef.current.settings
    const st = stateRef.current

    playAlarm(s.alarmSound, s.volume / 100)
    stopBackgroundAudio()
    hapticNotification('success')

    if (userId && st.startedAt) {
      const dur = mode === 'work' ? s.workDuration
        : mode === 'short_break' ? s.shortBreakDuration : s.longBreakDuration

      createPomodoroSession({
        userId,
        sessionType: mode,
        plannedMinutes: dur,
        actualSeconds: dur * 60,
        habitId: st.activeHabitId,
        taskText: st.activeTaskText,
        completed: true,
        startedAt: st.startedAt,
      }).then(() => {
        qc.invalidateQueries({ queryKey: ['pomodoro', 'stats'] })
      }).catch(() => {})
    }

    const autoStart = (mode === 'work' && s.autoStartBreaks)
      || (mode !== 'work' && s.autoStartWork)

    skipToNext()
    if (autoStart) setTimeout(startTimer, 500)
  }, [timeRemaining, status, mode, userId, skipToNext, startTimer, qc])

  // Visibility change - ilova backgroundga o'tganda pauza qilish
  useEffect(() => {
    function handleVisibility(): void {
      if (document.visibilityState === 'hidden') {
        const s = usePomodoroStore.getState()
        if (s.status === 'running') {
          s.pauseTimer()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])
}

// ── Background music control ──

export function usePomodoroAudio(): {
  playMusic: (url: string) => void
  stopMusic: () => void
} {
  const volume = usePomodoroStore((s) => s.settings.volume)

  const playMusic = useCallback((url: string): void => {
    playBackgroundAudio(url, volume)
  }, [volume])

  const stopMusic = useCallback((): void => {
    stopBackgroundAudio()
  }, [])

  // Volume change
  useEffect(() => {
    setBackgroundVolume(volume)
  }, [volume])

  return { playMusic, stopMusic }
}

// ── Tasks hook ──

export function usePomodoroTasks(): {
  tasks: readonly PomodoroTask[]
  isLoading: boolean
  addTask: (text: string) => void
  toggle: (id: string, completed: boolean) => void
  remove: (id: string) => void
  clearCompleted: () => void
} {
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const qc = useQueryClient()
  const key = pomodoroKeys.tasks(userId)

  type Task = PomodoroTask

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => fetchPomodoroTasks(userId),
    enabled: !!userId,
    staleTime: 120_000,
  })

  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey: key }),
    [qc, key],
  )

  const addMut = useMutation({
    mutationFn: (text: string) => createPomodoroTask(userId, text),
    onMutate: async (text: string) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<Task[]>(key)
      const optimistic: Task = {
        id: crypto.randomUUID(), user_id: userId, text,
        completed: false, sort_order: (prev?.length ?? 0),
        target_pomodoros: 1, completed_pomodoros: 0, habit_id: null,
        created_at: new Date().toISOString(),
      }
      qc.setQueryData<Task[]>(key, (old) => [...(old ?? []), optimistic])
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: invalidate,
  })
  const toggleMut = useMutation({
    mutationFn: ({ id, completed }: { id: string; completed: boolean }) =>
      togglePomodoroTask(id, completed),
    onMutate: async ({ id, completed }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<Task[]>(key)
      qc.setQueryData<Task[]>(key, (old) =>
        (old ?? []).map((t) => t.id === id ? { ...t, completed } : t),
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: invalidate,
  })
  const removeMut = useMutation({
    mutationFn: (id: string) => deletePomodoroTask(id),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<Task[]>(key)
      qc.setQueryData<Task[]>(key, (old) => (old ?? []).filter((t) => t.id !== id))
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: invalidate,
  })
  const clearMut = useMutation({
    mutationFn: () => clearCompletedPomodoroTasks(userId),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<Task[]>(key)
      qc.setQueryData<Task[]>(key, (old) => (old ?? []).filter((t) => !t.completed))
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: invalidate,
  })

  return {
    tasks: data ?? [],
    isLoading,
    addTask: useCallback((t: string) => addMut.mutate(t), [addMut]),
    toggle: useCallback((id: string, c: boolean) => toggleMut.mutate({ id, completed: c }), [toggleMut]),
    remove: useCallback((id: string) => removeMut.mutate(id), [removeMut]),
    clearCompleted: useCallback(() => clearMut.mutate(), [clearMut]),
  }
}

// ── Presets hook ──

export function usePomodoroPresets(): {
  presets: readonly PomodoroPreset[]
  isLoading: boolean
} {
  const { data, isLoading } = useQuery({
    queryKey: pomodoroKeys.presets,
    queryFn: fetchPomodoroPresets,
    staleTime: Infinity,
  })
  return { presets: data ?? [], isLoading }
}
